import * as React from 'react'
import gql from 'graphql-tag'

import { useQuery } from '@apollo/react-hooks'
import { EnterpriseContext } from '@thg-commerce/enterprise-core'
import {
  Country,
  Currency,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { EnterpriseProduct } from '@thg-commerce/enterprise-network/types/enterpriseTypes'

export interface ArticleContextInterface {
  products: Map<string, EnterpriseProduct>
}

export const ArticleContext = React.createContext<ArticleContextInterface>({
  products: new Map(),
})

export const buildQuery = (numberOfProducts: number) => {
  return gql`query products (
    $strict: Boolean!
    $currency: Currency!
    $shippingDestination: Country!
  ) {
    ${Array(numberOfProducts)
      .fill(0)
      .map(
        (_, index) => `
        product_${index}: product(
          sku: $product_${index},
          strict: $strict,
          currency: $currency,
          shippingDestination: $shippingDestination
        ) @client {
          sku
          inStock
          images {
            largeProduct
          }
          title
          url
          reviews {
            total
            averageScore
          }
          price @client
          defaultVariant @client
          cheapestVariant @client
          mostExpensiveVariant @client
          hasFromPrice @client
          productType @client
          content {
            key
            value {
              ... on ProductContentRichContentListValue {
                value { 
                  content {
                    content
                  }
                }
              }
            }
          }
        }`,
      )
      .join('\n')}
  }`
}

export const ArticleContextProvider = (props: {
  children?: React.ReactNode
  productSkus: string[]
}) => {
  const productsMap = new Map<string, EnterpriseProduct>()
  const EnterpriseCtx = React.useContext(EnterpriseContext)
  const queryVariables = {
    currency: EnterpriseCtx.currency as Currency,
    shippingDestination: EnterpriseCtx.shippingDestination.code as Country,
    strict: false,
  }

  const skuSet = new Set<string>(props.productSkus)
  if (skuSet.size > 0) {
    Array.from(skuSet).forEach((item, index) => {
      queryVariables[`product_${index}`] = item
    })

    const { data } = useQuery<
      { [key: string]: EnterpriseProduct | null },
      { strict: Boolean; currency: Currency; shippingDestination: Country }
    >(buildQuery(skuSet.size), {
      variables: queryVariables,
    })

    if (data) {
      Array.from(skuSet).forEach((sku: string, index) => {
        const productData = data[`product_${index}`]
        productData && productsMap.set(sku, productData)
      })
    }
  }

  return (
    <ArticleContext.Provider value={{ products: productsMap }}>
      {props.children}
    </ArticleContext.Provider>
  )
}
